import { Box, ThemeProvider } from "@mui/material";
import type { IRecipe, ISavedRecipes } from "../types/recipe";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import Theme from "../utils/theme";
import SavedRecipe from "../components/SavedRecipe";
import NotFound from "../components/NotFound";
import LoadingWithTip from "../components/LoadingWithTip";

interface ISingleRecipeProps {
    type?: "recipeId" | "recipeCode";
}

const SingleRecipe: React.FC<ISingleRecipeProps> = ({ type }) => {
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const [recipe, setRecipe] = useState<IRecipe | ISavedRecipes | null>(null);
    const [error, setError] = useState(true);
    const [loading, setLoading] = useState(true);

    const { objectId: RouteId } = useParams();
    const parseSavedRecipeData = useCallback((jsonData: any) => {
        const output = {
            name: jsonData.name,
            description: jsonData.description,
            ingredients: jsonData.ingredients,
            procedure: jsonData.procedure,
            tags: jsonData.tags ?? [],
            image: { url: jsonData.image ? jsonData.image.url : "" },
        };
        return output;
    }, []);

    useEffect(() => {
        setError(false);
        setLoading(true);

        let endpoint;

        if (type === "recipeId") endpoint = `${process.env.REACT_APP_API_URL}/recipes/fetchById`;
        else endpoint = `${process.env.REACT_APP_API_URL}/recipes/fetchByCode`;

        axios
            .get(endpoint, { params: { routeId: RouteId } })
            .then((res) => {
                if (type === "recipeId") setRecipe(res.data.body);
                else {
                    if (res.data.body.type === "Recipe") {
                        setRecipe(parseSavedRecipeData(res.data.body.recipe));
                    } else if (res.data.body.type === "Generation") {
                        const parsedJsonData = parseSavedRecipeData(res.data.body.recipe);
                        setRecipe(parsedJsonData);
                    }
                }
            })
            .catch((err) => {
                setError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [RouteId, parseSavedRecipeData, type]);

    if (loading)
        return (
            <Box
                sx={{
                    height: "calc(100dvh - 200px)",
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <LoadingWithTip />
            </Box>
        );
    else if (error || !recipe) return <NotFound />;

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Box sx={{ width: "100vw", maxWidth: "1200px", display: "flex", mt: 6 }}>
                    <SavedRecipe
                        containerHeight="calc(100dvh - 150px)"
                        recipeData={recipe as ISavedRecipes}
                        setRecipeData={setRecipe as React.Dispatch<React.SetStateAction<ISavedRecipes>>}
                        disableAllButtons={type === "recipeCode"}
                    />
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default SingleRecipe;
